import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { categories, videos } from '@/mocks/data';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'dianshiju');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredVideos = videos.filter((v) => v.categorySlug === activeSlug);

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': `${currentCategory.name} - 豆花视频免费在线观看`,
    'description': currentCategory.description,
    'url': `https://www.douhuavideo.com/category/${activeSlug}`,
    'isPartOf': {
      '@type': 'WebSite',
      'name': '豆花视频',
      'url': 'https://www.douhuavideo.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: currentCategory.name },
              ]}
            />
          </div>

          {/* Category Header */}
          <section className="px-4 md:px-6 py-8 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                  <i className={`${currentCategory.icon} text-2xl`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    {currentCategory.description}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="px-4 md:px-6 py-4 border-b border-background-200 bg-background-50 sticky top-16 z-30">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-1 overflow-x-auto pb-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Video Grid */}
          <section className="px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-bold text-foreground-900 font-heading">
                  共 {filteredVideos.length} 部作品
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500">排序：</span>
                  <button className="px-3 py-1 text-xs rounded-full bg-primary-500 text-background-50 font-medium whitespace-nowrap cursor-pointer">
                    推荐
                  </button>
                  <button className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 font-medium whitespace-nowrap cursor-pointer">
                    最新
                  </button>
                  <button className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 font-medium whitespace-nowrap cursor-pointer">
                    评分
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} {...video} />
                ))}
              </div>
              {filteredVideos.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}